import * as THREE from 'three';
import { POWERUP_NONE } from './powerups.js';

export const RUPTURE_PLATFORM_POWERUP_COUNT=4;
export const RUPTURE_ROUTE_POWERUP_COUNT=3;
export const RUPTURE_POWERUP_REFRESH_DELAY=5;

const PLATFORM_EDGE_OFFSET=2.45;
const PLATFORM_HEIGHT_OFFSET=2.1;
const ROUTE_PROGRESS=Object.freeze([.28,.52,.76]);
const ROUTE_SIDE_OFFSETS=Object.freeze([3.2,4.4,3.35]);
const ROUTE_HEIGHT_OFFSETS=Object.freeze([2.35,3.15,2.45]);

function crate(id,position,role,stageIndex){
  return {
    id,
    x:position.x,
    y:position.y,
    z:position.z,
    available:true,
    respawnTimer:0,
    lastPowerup:POWERUP_NONE,
    ruptureRole:role,
    ruptureStageIndex:stageIndex,
  };
}

function outerTile(stage,direction){
  let selected=null;
  let selectedProjection=-Infinity;
  let selectedPerpendicular=Infinity;
  const perpendicular=new THREE.Vector3(-direction.z,0,direction.x);
  for(const tile of stage?.tiles||[]){
    const position=tile.initialPosition||tile.position;
    if(!position)continue;
    const relative=position.clone().sub(stage.center);
    const projection=relative.dot(direction);
    const perpendicularDistance=Math.abs(relative.dot(perpendicular));
    if(projection>selectedProjection+.001
      ||(Math.abs(projection-selectedProjection)<=.001&&perpendicularDistance<selectedPerpendicular)){
      selected=position;
      selectedProjection=projection;
      selectedPerpendicular=perpendicularDistance;
    }
  }
  return selected?.clone?.()||stage.center.clone();
}

export function createRupturePlatformPowerupCrates(arena,stageIndex){
  const stage=arena?.getStage?.(stageIndex);
  if(!stage)return [];
  const directions=[
    new THREE.Vector3(1,0,0),
    new THREE.Vector3(0,0,1),
    new THREE.Vector3(-1,0,0),
    new THREE.Vector3(0,0,-1),
  ];
  return directions.slice(0,RUPTURE_PLATFORM_POWERUP_COUNT).map((direction,index)=>{
    const position=outerTile(stage,direction).addScaledVector(direction,PLATFORM_EDGE_OFFSET);
    position.y=stage.top+PLATFORM_HEIGHT_OFFSET;
    return crate(`rupture-${stageIndex}-platform-${index+1}`,position,'platform',stageIndex);
  });
}

export function createRuptureRoutePowerupCrates(arena,currentIndex,nextIndex){
  const current=arena?.getStage?.(currentIndex);
  const next=arena?.getStage?.(nextIndex);
  if(!current||!next)return [];
  const route=next.center.clone().sub(current.center);
  route.y=0;
  if(route.lengthSq()<.001)return [];
  route.normalize();
  const perpendicular=new THREE.Vector3(-route.z,0,route.x);
  const sideSign=currentIndex%2===0?1:-1;
  return ROUTE_PROGRESS.slice(0,RUPTURE_ROUTE_POWERUP_COUNT).map((progress,index)=>{
    const position=current.center.clone().lerp(next.center,progress);
    const alternating=index%2===0?sideSign:-sideSign;
    position.addScaledVector(perpendicular,ROUTE_SIDE_OFFSETS[index]*alternating);
    position.y=THREE.MathUtils.lerp(current.top,next.top,progress)+ROUTE_HEIGHT_OFFSETS[index];
    return crate(
      `rupture-${currentIndex}-${nextIndex}-route-${index+1}`,
      position,
      'route',
      currentIndex,
    );
  });
}

export class RupturePowerupDirector{
  constructor(arena){
    this.arena=arena;
    this.crates=[];
    this.phaseId=null;
    this.platformIndex=-1;
    this.routeKey=null;
    this.combatRefreshed=false;
    const state=this.arena?.state?.();
    if(state){
      this.enterCombat(state.platformIndex);
      this.phaseId=state.phase.id;
      if(state.nextPlatformIndex!==null)this.appendRoute(state.platformIndex,state.nextPlatformIndex);
      this.refreshCombatCrates(state);
    }
  }

  replaceCrates(crates){
    this.crates.splice(0,this.crates.length,...crates);
  }

  enterCombat(stageIndex){
    this.platformIndex=stageIndex;
    this.routeKey=null;
    this.combatRefreshed=false;
    this.replaceCrates(createRupturePlatformPowerupCrates(this.arena,stageIndex));
  }

  appendRoute(currentIndex,nextIndex){
    const routeKey=`${currentIndex}:${nextIndex}`;
    if(this.routeKey===routeKey)return false;
    this.crates.push(...createRuptureRoutePowerupCrates(this.arena,currentIndex,nextIndex));
    this.routeKey=routeKey;
    return true;
  }

  refreshCombatCrates(state){
    if(state.phase.id!=='combat'||this.combatRefreshed)return false;
    const delay=Math.max(0,Number(this.arena?.rules?.powerupRefreshDelay)
      ||RUPTURE_POWERUP_REFRESH_DELAY);
    const elapsed=Math.max(0,state.phase.duration-state.phase.remaining);
    if(elapsed+1e-6<delay)return false;
    let changed=false;
    this.crates.forEach((entry)=>{
      if(entry.ruptureRole!=='platform'||entry.ruptureStageIndex!==state.platformIndex)return;
      if(!entry.available){
        entry.available=true;
        entry.respawnTimer=0;
        changed=true;
      }
    });
    this.combatRefreshed=true;
    return changed;
  }

  update(){
    const state=this.arena?.state?.();
    if(!state)return false;
    let changed=false;
    if(state.phase.id==='combat'
      &&(this.phaseId!=='combat'||this.platformIndex!==state.platformIndex)){
      this.enterCombat(state.platformIndex);
      changed=true;
    }
    if(state.phase.id==='warning'&&state.nextPlatformIndex!==null){
      changed=this.appendRoute(state.platformIndex,state.nextPlatformIndex)||changed;
    }
    changed=this.refreshCombatCrates(state)||changed;
    this.phaseId=state.phase.id;
    return changed;
  }
}
